/**
 * The transaction journal: durable, cross-tab store of one row per signing step.
 *
 * Reads are synchronous. That is the whole reason this is `localStorage` and not
 * IndexedDB — the very first render after a reload already has the correct state,
 * so there is no flash of "idle" before an async read resolves, and therefore no
 * visible UI change versus today.
 *
 * Referential stability matters here: `getEntry` returns the same object
 * reference until that row actually changes, so it is safe to feed straight into
 * `useSyncExternalStore` without tearing or render loops. `machine.transition`
 * cooperates by returning its input unchanged for no-op events.
 */

import { stringify, parse } from "./codec";
import * as storage from "./storage";
import { transition, isOpen, isTerminal, EVENTS } from "./machine";
import {
  JOURNAL_KEY,
  SCHEMA_VERSION,
  entryKey,
  newEntry,
  scopeMatches,
} from "./keys";

let entries = Object.create(null);
let listeners = new Set();
let loaded = false;

function readFromStorage() {
  const raw = storage.getItem(JOURNAL_KEY);
  const parsed = parse(raw, null);

  // Anything we do not recognise is discarded rather than migrated. A journal is
  // recoverable state, not a source of truth — starting clean is always safe,
  // and it guarantees a corrupt payload can never throw during app boot.
  if (!parsed || parsed.version !== SCHEMA_VERSION || !parsed.entries) {
    return Object.create(null);
  }

  const now = Date.now();
  const out = Object.create(null);
  Object.keys(parsed.entries).forEach((key) => {
    const entry = parsed.entries[key];
    if (!entry || typeof entry !== "object" || !entry.key) return;
    // Drop expired and long-settled rows on the way in.
    if (entry.expiresAt && entry.expiresAt < now) return;
    if (isTerminal(entry.status) && entry.updatedAt && now - entry.updatedAt > 60 * 60 * 1000) {
      return;
    }
    out[key] = entry;
  });
  return out;
}

function ensureLoaded() {
  if (loaded) return;
  loaded = true;
  entries = readFromStorage();
}

function persist() {
  const payload = stringify({ version: SCHEMA_VERSION, entries });
  storage.setItem(JOURNAL_KEY, payload, gc);
  storage.announce(JOURNAL_KEY);
}

function notify() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.error("[tx] journal listener threw", err);
    }
  });
}

/**
 * Remove expired and settled rows. Also used as the quota-exceeded recovery
 * hook, which is why it must be safe to call at any time.
 */
export function gc(now = Date.now()) {
  ensureLoaded();
  let changed = false;
  Object.keys(entries).forEach((key) => {
    const entry = entries[key];
    const expired = entry.expiresAt && entry.expiresAt < now;
    const settled =
      isTerminal(entry.status) && entry.updatedAt && now - entry.updatedAt > 60 * 60 * 1000;
    if (expired || settled) {
      delete entries[key];
      changed = true;
    }
  });
  return changed;
}

export function getEntry(key) {
  ensureLoaded();
  return entries[key] || null;
}

/** Create the row if absent. Returns the existing row untouched otherwise. */
export function ensureEntry(descriptor) {
  ensureLoaded();
  const key = entryKey(descriptor);
  if (!entries[key]) {
    entries[key] = newEntry(descriptor);
    persist();
    notify();
  }
  return entries[key];
}

/**
 * Apply a machine event to a row. No-ops (duplicate receipts, late events on a
 * terminal row) neither persist nor notify, which keeps React quiet while the
 * receipt watcher polls.
 */
export function apply(key, event, payload) {
  ensureLoaded();
  const current = entries[key];
  if (!current) return null;
  const next = transition(current, event, payload);
  if (next === current) return current;
  entries[key] = next;
  persist();
  notify();
  return next;
}

export function removeEntry(key) {
  ensureLoaded();
  if (!entries[key]) return;
  delete entries[key];
  persist();
  notify();
}

/** Every row for the given wallet+chain that still needs an answer. */
export function openEntriesForScope(scope) {
  ensureLoaded();
  return Object.keys(entries)
    .map((key) => entries[key])
    .filter((entry) => scopeMatches(entry, scope) && isOpen(entry));
}

export function allEntries() {
  ensureLoaded();
  return Object.keys(entries).map((key) => entries[key]);
}

export function subscribe(listener) {
  ensureLoaded();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Adopt the stored rows, KEEPING the object identity of every row whose content
 * did not actually change.
 *
 * These objects go straight to React through `useSyncExternalStore`, and a
 * consumer's `useEffect([error])` re-runs on identity alone. A wholesale swap
 * therefore replays outcomes the screen has already reported — one rejected
 * withdrawal, two identical error toasts. Comparing through the codec is cheap
 * here: the journal holds a handful of small rows, not a dataset.
 */
function reconcile(previous, fresh) {
  const out = Object.create(null);
  let changed = Object.keys(previous).length !== Object.keys(fresh).length;
  Object.keys(fresh).forEach((key) => {
    const before = previous[key];
    const same = before && stringify(before) === stringify(fresh[key]);
    out[key] = same ? before : fresh[key];
    if (!same) changed = true;
  });
  return { entries: out, changed };
}

/**
 * Another tab wrote — or, on browsers where `BroadcastChannel` echoes a local
 * announcement back to this tab's own subscriber, we did. Re-read either way,
 * but stay silent when nothing actually moved.
 */
function onExternalChange() {
  const next = reconcile(entries, readFromStorage());
  entries = next.entries;
  if (next.changed) notify();
}

let unsubscribeStorage = null;
export function start() {
  ensureLoaded();
  if (!unsubscribeStorage) {
    unsubscribeStorage = storage.subscribe(JOURNAL_KEY, onExternalChange);
  }
  if (gc()) {
    persist();
    notify();
  }
}

export function stop() {
  if (unsubscribeStorage) {
    unsubscribeStorage();
    unsubscribeStorage = null;
  }
}

export const __testing = {
  reset() {
    entries = Object.create(null);
    listeners = new Set();
    loaded = false;
    storage.removeItem(JOURNAL_KEY);
  },
  /**
   * Drop every scrap of in-memory state and re-read from storage — exactly what
   * a page reload does to this module, without needing a second module registry
   * (which would give the test a second copy of React and break hooks).
   */
  simulateReload() {
    entries = Object.create(null);
    listeners = new Set();
    loaded = false;
    ensureLoaded();
  },
  raw: () => entries,
  EVENTS,
};
